/* eslint-disable react/no-danger */
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  CheckIcon,
  ClickAwayListener,
  DeleteIcon,
  Divider,
  Down1Icon,
  Drag1Icon,
  Edit1Icon,
  FAQsIcon,
  GKButton,
  Grid2,
  IconButton,
  KebabMenu2Icon,
  NoData,
  Plus1Icon,
  PopConfirm,
  Popper,
  Show,
  Typography,
} from "components";
import { useFetchQuery, useMutation } from "hooks/useQueries";
import { FaqListInterface } from "interfaces/WebsiteInterface";
import { map } from "lodash";
import { useState } from "react";
import { FormProvider, useFieldArray, useForm } from "react-hook-form";
import axiosInstance from "utils/axiosInstance";
import { refetchQuery } from "utils/helpers";
import FaqModal from "./FaqModal";

const FaqSettings = () => {
  const form = useForm<FaqListInterface>();

  const [openFaqModal, setOpenFaqModal] = useState<boolean | null>(false);

  const [openDeleteConfirmation, setOpenDeleteConfirmation] = useState<
    boolean | null
  >(false);

  const [dragIndex, setDragIndex] = useState<number | null>(null);

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

  const [expandedAccordian, setExpandedAccordian] = useState(null);

  const [selectedQuestion, setSelectedQuestion] = useState(null);

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(anchorEl ? null : event.currentTarget);
  };

  const open = Boolean(anchorEl);
  const id = open ? "simple-popper" : undefined;

  useFetchQuery<{
    data: {
      data: FaqListInterface;
    };
  }>({
    key: ["GET_ALL_ALL_FAQS"],
    route: `/faqs/`,
    success: (response: any) => {
      setOpenFaqModal(false);
      form.reset(response?.data?.data);
    },
  });

  const { mutate, isLoading } = useMutation({
    mutationFn: (data: FaqListInterface) =>
      axiosInstance.patch(`/faqs/update-faqs/`, data),
    onSuccess: () => {
      refetchQuery("GET_ALL_ALL_FAQS");
    },
  });

  const handleSubmit = (values: FaqListInterface) => {
    mutate(values);
  };

  const { append, fields } = useFieldArray({
    name: "questionsAnswers",
    control: form.control,
  });

  return fields?.length === 0 ? (
    <NoData
      customImg={
        <FAQsIcon color="var(--neutral-100)" width={100} height={100} />
      }
      additionalComponent={
        <GKButton
          variant="contained"
          startIcon={<Plus1Icon />}
          sx={{ mt: 1 }}
          onClick={() => {
            append({
              answer: "",
              question: "",
            });
            setSelectedQuestion(fields?.length);
            setOpenFaqModal(true);
          }}
        >
          Add New Question
        </GKButton>
      }
      text="No FAQs Added Yet"
      subText="Create answers to your client most common questions — they’ll appear directly on your website FAQ section."
    />
  ) : (
    <FormProvider {...form}>
      <form onSubmit={form.handleSubmit(handleSubmit)}>
        <Grid2 container spacing={2}>
          <Grid2
            size={12}
            maxHeight={"calc(100vh - 240px)"}
            minHeight={"calc(100vh - 240px)"}
            overflow={"auto"}
            py={2}
          >
            {map(fields, (_, index) => {
              return (
                <Accordion
                  draggable
                  onDragStart={() => setDragIndex(index)}
                  onDragOver={(e) => e.preventDefault()}
                  onDrop={() => {
                    if (dragIndex === null || dragIndex === index) return;

                    const current = form.getValues("questionsAnswers");
                    const movedItem = current[dragIndex];
                    const remainingItems = current.filter(
                      (_, i) => i !== dragIndex,
                    );

                    const newItems = [
                      ...remainingItems.slice(0, index),
                      movedItem,
                      ...remainingItems.slice(index),
                    ];

                    form.setValue("questionsAnswers", newItems);
                    setDragIndex(null);
                  }}
                  onDragEnd={() => setDragIndex(null)}
                  onDragEnter={(e) => {
                    e.preventDefault();
                  }}
                  onDragLeave={(e) => {
                    e.preventDefault();
                  }}
                  onChange={() => {
                    setExpandedAccordian(
                      expandedAccordian !== index ? index : null,
                    );
                  }}
                  expanded={expandedAccordian === index}
                  key={index}
                  elevation={0}
                  sx={{
                    borderBottom: "1px solid var(--neutral-100)",
                    "&::before": {
                      display: "none",
                    },
                    opacity: dragIndex === index ? 0.5 : 1,
                  }}
                >
                  <AccordionSummary expandIcon={""}>
                    <Box
                      width={"100%"}
                      display={"flex"}
                      alignContent={"center"}
                      justifyContent={"space-between"}
                    >
                      <Box
                        display={"flex"}
                        alignItems={"center"}
                        justifyContent={"space-between"}
                        gap={1}
                      >
                        <IconButton
                          sx={{
                            cursor: "move",
                          }}
                        >
                          <Drag1Icon color={"var(--neutral-500)"} />
                        </IconButton>{" "}
                        <Typography
                          color="var(--black)"
                          fontWeight={600}
                          display={"flex"}
                          alignItems={"center"}
                        >
                          {form.watch(`questionsAnswers.${index}.question`)}
                        </Typography>
                      </Box>
                      <Box
                        display={"flex"}
                        alignItems={"center"}
                        justifyContent={"space-between"}
                        gap={2}
                      >
                        <IconButton>
                          <Down1Icon
                            color={
                              expandedAccordian === index
                                ? "var(--neutral-500)"
                                : "var(--black)"
                            }
                            style={{
                              transition: "all 0.3s",
                              transform:
                                expandedAccordian === index
                                  ? "rotate(180deg)"
                                  : "",
                            }}
                          />
                        </IconButton>{" "}
                        <Divider orientation="vertical" />
                        <IconButton
                          onClick={(e) => {
                            e.stopPropagation();
                            handleClick(e);
                            setSelectedQuestion(index);
                            form.setValue("isEditing", true);
                          }}
                        >
                          <KebabMenu2Icon color="var(--black)" />
                        </IconButton>
                      </Box>
                    </Box>
                  </AccordionSummary>
                  <AccordionDetails>
                    <Grid2 container spacing={2}>
                      <Grid2 size={12} px={5}>
                        <div
                          dangerouslySetInnerHTML={{
                            __html: _?.answer,
                          }}
                        />
                      </Grid2>
                    </Grid2>
                  </AccordionDetails>
                </Accordion>
              );
            })}
            <GKButton
              startIcon={<Plus1Icon />}
              sx={{ mt: 1 }}
              onClick={() => {
                append({
                  answer: "",
                  question: "",
                });
                setSelectedQuestion(fields?.length);
                setOpenFaqModal(true);
              }}
            >
              Add New Question
            </GKButton>
          </Grid2>
          <Grid2 size={12} display={"flex"} justifyContent={"flex-end"}>
            <GKButton
              variant="contained"
              type="submit"
              loading={isLoading}
              startIcon={<CheckIcon />}
            >
              Save Changes
            </GKButton>
          </Grid2>
        </Grid2>
        <ClickAwayListener
          onClickAway={() => {
            setAnchorEl(null);
          }}
        >
          <Popper
            id={id}
            open={open}
            anchorEl={anchorEl}
            placement="bottom-end"
          >
            <Box
              sx={{
                py: 1,
                bgcolor: "var(--white)",
                boxShadow: "4px 4px 12px 0px #00000040",
                borderRadius: 2,
                display: "flex",
                flexDirection: "column",
              }}
            >
              <GKButton
                startIcon={<Edit1Icon />}
                color="dark"
                onClick={() => {
                  setAnchorEl(null);
                  setOpenFaqModal(true);
                }}
              >
                Edit
              </GKButton>
              <GKButton
                loading={isLoading}
                startIcon={<DeleteIcon />}
                color="error"
                onClick={() => {
                  setOpenDeleteConfirmation(true);
                  setAnchorEl(null);
                }}
              >
                Delete
              </GKButton>
            </Box>
          </Popper>
        </ClickAwayListener>
        <Show.When isTrue={openFaqModal}>
          <FaqModal
            open={openFaqModal}
            setOpen={setOpenFaqModal}
            selectedQuestion={selectedQuestion}
          />
        </Show.When>
        <Show.When isTrue={openDeleteConfirmation}>
          <PopConfirm
            anchorEl={null}
            open={openDeleteConfirmation}
            transformOrigin={{ vertical: "center", horizontal: "center" }}
            anchorOrigin={{ vertical: "center", horizontal: "center" }}
            setOpen={setOpenDeleteConfirmation}
            onSuccess={() => {
              const newValues = fields?.filter(
                (_, index) => selectedQuestion !== index,
              );
              mutate({
                ...form.getValues(),
                questionsAnswers: newValues,
              });
            }}
          />
        </Show.When>
      </form>
    </FormProvider>
  );
};

export default FaqSettings;
